/**
 * What a student may do with one interview, in one place, so the Interviews
 * list and the application page offer the same buttons in the same states.
 *
 * - Accept and "Can't make it" are one answer per slot: once given, the
 *   chosen one reads "Accepted" / "Declined" and both are disabled. A company
 *   reschedule resets the answer to PENDING (the API does this) and both
 *   come back. The API refuses a second answer with a 409 too.
 * - "Join the call" exists only for a video interview the student accepted;
 *   after declining it stays visible but disabled.
 * - "Request another time" is offered while the round is booked and ahead,
 *   answered or not -- but not after declining, when the company decides what
 *   happens next. It is disabled (with the API's reason) when too close, or
 *   the allowance is used up.
 * - While their request is with the company, the original time is not theirs
 *   to accept or decline: the answer buttons step aside for "Withdraw".
 * - Once the time has passed without an answer (`lapsed`), Accept and "Can't
 *   make it" stay visible but disabled, saying why; the API refuses an answer
 *   after the start. The company can still move the round, which asks again.
 *
 * Every disabled action carries `title`, the tooltip saying why.
 */
import { hasOpenRequest } from './reschedule';

export const DECLINED_TOOLTIP = 'You declined this interview';
export const LAPSED_TOOLTIP =
  'The interview time has passed without an answer, so it can no longer be accepted or declined. The company can choose a new time.';

export function studentInterviewActions(interview) {
  const booked = interview?.status === 'SCHEDULED';
  const ahead = Boolean(interview?.can_respond);
  const response = interview?.student_response ?? 'PENDING';
  const open = hasOpenRequest(interview);
  const accepted = response === 'ACCEPTED';
  const declined = response === 'DECLINED';
  const joinUrl =
    booked && interview.mode === 'VIDEO' && interview.meeting_url ? interview.meeting_url : null;

  const answering = ahead && !open;
  // Still booked, the time has passed and they never answered. A request
  // left open past the start comes back EXPIRED, so `open` is false here.
  const lapsed = booked && !ahead && response === 'PENDING' && !open;
  return {
    lapsed,
    accept: {
      show: answering || lapsed,
      disabled: accepted || declined || lapsed,
      label: accepted ? 'Accepted' : 'Accept',
      title: lapsed
        ? LAPSED_TOOLTIP
        : accepted
          ? 'You accepted this interview'
          : declined
            ? `${DECLINED_TOOLTIP}. The company will decide what happens next.`
            : null,
    },
    decline: {
      show: answering || lapsed,
      disabled: accepted || declined || lapsed,
      label: declined ? 'Declined' : 'Can’t make it',
      title: lapsed
        ? LAPSED_TOOLTIP
        : declined
          ? DECLINED_TOOLTIP
          : accepted
            ? 'You accepted this interview. Need a different time? Request another time instead.'
            : null,
    },
    join: {
      show: Boolean(joinUrl) && (accepted || declined),
      disabled: declined,
      url: joinUrl,
      title: declined ? DECLINED_TOOLTIP : null,
    },
    request: {
      show: ahead && !open && !declined,
      disabled: !interview?.can_request_reschedule,
      title: interview?.can_request_reschedule
        ? null
        : (interview?.reschedule_blocked_reason ?? null),
    },
    withdraw: { show: ahead && open },
  };
}
